import { useUpdateCoreConsents } from "@tandemdiabetes/sugarmate-api-react-client"
import { useToasterContext } from "@tandemdiabetes/sugarmate-react-components"
import type {
  ComponentProps,
  FormEventHandler,
} from "react"
import {
  useCallback,
  useState,
} from "react"
import { RequiredConsentNoticesForm } from "src/components"
import { useOverlayLoader } from "src/contexts"
import { usePendingConsents } from "src/hooks/consent/usePendingConsents"
import { GenericErrorToast } from "src/toasts"

type Form = ComponentProps<typeof RequiredConsentNoticesForm>["form"]

interface Props {
  onSubmitted: FormEventHandler<HTMLFormElement>;
  children?: ComponentProps<typeof RequiredConsentNoticesForm>["children"];
}

export const RequiredConsentsPage = (props: Props) => {
  const {
    onSubmitted, children,
  } = props
  const { showToast } = useToasterContext()

  const { data: consents } = usePendingConsents()
  const updateConsents = useUpdateCoreConsents()

  const { setIsOverlayLoaderVisible } = useOverlayLoader()

  const [
    form,
    setForm,
  ] = useState<Form>({
    consents: {},
  })

  const updateForm = useCallback(
    (value: Partial<Form>) => {
      setForm({
        ...form,
        ...value,
      })
    },
    [
      form,
      setForm,
    ],
  )

  const onRequiredConsentsSubmit: FormEventHandler<HTMLFormElement> =
    useCallback(
      (e) => {
        void (async (ev) => {
          ev.preventDefault()
          ev.stopPropagation()

          try {
            if (!updateConsents) {
              throw new Error("Update consents hook is not available")
            }

            if (!consents) {
              throw new Error("Consents data is not available")
            }

            setIsOverlayLoaderVisible(true)

            await updateConsents({
              metadataId: consents.metadataId,
              metadataVersion: consents.metadataVersion,
              consents: [
                ...(consents.required?.consents || []).map((consent) => ({
                  policyId: consent.policyId,
                  version: consent.version,
                  accepted: form.consents[consent.policyId],
                })),
              ],
            })

            onSubmitted(ev)
          } catch {
            showToast(
              "edit-consents-error-toast",
              GenericErrorToast,
            )
          } finally {
            setIsOverlayLoaderVisible(false)
          }
        })(e)
      },
      [
        onSubmitted,
        updateConsents,
        consents,
        form,
        setIsOverlayLoaderVisible,
        showToast,
      ],
    )

  return (
    <RequiredConsentNoticesForm
      form={form}
      updateForm={updateForm}
      onSubmit={onRequiredConsentsSubmit}
      consents={consents?.required}
    >
      {children}
    </RequiredConsentNoticesForm>
  )
}
